import * as React from 'react'
import type { AppliedSettingModel } from '@/app/models/timesheetModel'
import { FieldsetSwitch } from '@/modules/settings/components/FieldsetSwitch'
import { FieldsetSelect } from '@/modules/settings/components/FieldsetSelect'
import { FieldsetInput } from '@/modules/settings/components/FieldsetInput'
import {
  WEEK_FREE_DAYS,
  HOLIDAY_START_HOURS_OPTIONS,
  NIGHTTIME_START_HOURS_OPTIONS,
  WORKTIME_LIMIT_HOURS_OPTIONS,
  MINUTES_OPTIONS,
} from '@/modules/settings/pages/timesheet-basic/options'
import { TripleTimeLikeFields } from '@/modules/settings/pages/timesheet-basic/TripleTimeLikeFields'
import {
  BREAK_MAPPING_DESTINATIONS,
  SHORT_BRAKE_MODES,
  SHORT_BREAK_TYPES,
  LUMPSUM_AMOUNTS_COVERED_OPTIONS,
  PLANNED_HOURS_ROUNDINGS,
  CALCULATION_ROUNDINGS,
  TIMESHEET_MINIMAL_PERIOD_SOURCES,
} from '@/modules/settings/pages/timesheet-additional/options'
import {
  getFieldSpec,
  getFieldEditability,
  toCamelKey,
} from './explainSettingFields'
import type { EditableValue } from './useExplainEditableSettings'

interface EditableSettingFieldProps {
  settingKey: string
  setting: AppliedSettingModel | undefined
  value: EditableValue
  onChange: (next: EditableValue) => void
  formatValue: (setting: AppliedSettingModel | undefined) => string
  tSafe: (key: string, fallback: string) => string
  disabled?: boolean
  /**
   * Read-only suffix for composite time pickers — settings page shows the
   * derived end time (e.g. " - 05:00:00" for night work).
   */
  readonlySuffix?: string | null
}

// camelCase DTO field name → option array for the inline-edit dropdowns. Reuses
// the same arrays the settings page uses, so we never drift.
const SELECT_OPTIONS: Record<string, ReadonlyArray<number | string>> = {
  weekFreeDay: WEEK_FREE_DAYS,
  breaksMapping: BREAK_MAPPING_DESTINATIONS,
  shortBreakMode: SHORT_BRAKE_MODES,
  shortBreakType: SHORT_BREAK_TYPES,
  showAmountsCoveredByLumpsum: LUMPSUM_AMOUNTS_COVERED_OPTIONS,
  plannedHoursRounding: PLANNED_HOURS_ROUNDINGS,
  calculationRounding: CALCULATION_ROUNDINGS,
  timesheetMinimalPeriods: TIMESHEET_MINIMAL_PERIOD_SOURCES,
}

const OVERHOURS_LIMIT_MAX = 416

// Backend AppliedSetting key (PascalCase) → hour options for the composite picker.
// Minutes are always MINUTES_OPTIONS, matching the settings page.
const TIME_COMPOSITE_HOURS: Record<string, ReadonlyArray<number>> = {
  HolidayStartTime: HOLIDAY_START_HOURS_OPTIONS,
  NightTimeStartHour: NIGHTTIME_START_HOURS_OPTIONS,
  WorkTimeLimitIntervalStartTime: WORKTIME_LIMIT_HOURS_OPTIONS,
}

const splitHHMM = (value: EditableValue): [number, number] => {
  const str = String(value || '00:00')
  const [h, m] = str.split(':')
  return [parseInt(h, 10) || 0, parseInt(m, 10) || 0]
}

const joinHHMM = (h: number, m: number): string =>
  `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`

export const EditableSettingField: React.FC<EditableSettingFieldProps> = ({
  settingKey,
  setting,
  value,
  onChange,
  formatValue,
  tSafe,
  disabled = false,
  readonlySuffix = null,
}) => {
  const spec = getFieldSpec(settingKey)
  const label = tSafe(spec.labelKey, settingKey)
  const editability = getFieldEditability(settingKey, setting?.type)
  const isReadonly = disabled || editability.startsWith('readonly')

  if (editability === 'editable-bool' && !isReadonly) {
    return (
      <FieldsetSwitch
        label={label}
        checked={Boolean(value)}
        onChange={(e) => onChange((e.target as HTMLInputElement).checked)}
        disabled={false}
      />
    )
  }

  if (editability === 'editable-select' && !isReadonly) {
    const dtoField = toCamelKey(settingKey)
    const options = SELECT_OPTIONS[dtoField]
    if (options) {
      // Each option label is produced by the same formatter the read-only path
      // uses, so the dropdown shows exactly what the settings page shows.
      const selectOptions = options.map((opt) => ({
        key: typeof opt === 'number' ? opt : opt,
        value: String(opt),
        label: formatValue({
          key: settingKey,
          value: String(opt),
          type: setting?.type ?? 'enum:unknown',
        }),
      }))
      return (
        <FieldsetSelect
          label={label}
          name={dtoField}
          value={String(value)}
          options={selectOptions}
          handleChange={(nextStr, { option }) => {
            const opt = option?.key
            if (typeof opt === 'number') {
              onChange(opt)
            } else if (typeof opt === 'string') {
              onChange(opt)
            } else {
              // Fall back to coercing the string value
              const asNum = Number(nextStr)
              onChange(Number.isNaN(asNum) ? nextStr : asNum)
            }
          }}
        />
      )
    }
    // No options registered → degrade to read-only display.
  }

  if (editability === 'editable-time-composite' && !isReadonly) {
    const hourOptions = TIME_COMPOSITE_HOURS[settingKey] ?? HOLIDAY_START_HOURS_OPTIONS
    const [hours, minutes] = splitHHMM(value)
    return (
      <fieldset className="tw-d-form-control tw-w-full">
        <label className="tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
          {label}
        </label>
        <TripleTimeLikeFields
          hour={{
            options: [...hourOptions],
            value: String(hours),
            name: `${settingKey}-hours`,
            handleChange: (next) => onChange(joinHHMM(next, minutes)),
          }}
          minute={{
            options: [...MINUTES_OPTIONS],
            value: String(minutes),
            name: `${settingKey}-minutes`,
            handleChange: (next) => onChange(joinHHMM(hours, next)),
          }}
          readonly={readonlySuffix ? { value: readonlySuffix } : undefined}
        />
      </fieldset>
    )
  }

  if (editability === 'editable-input-hours' && !isReadonly) {
    const numeric = typeof value === 'number' ? value : Number(value) || 0
    return (
      <FieldsetInput
        label={label}
        type="number"
        min={0}
        max={OVERHOURS_LIMIT_MAX}
        value={String(numeric)}
        onChange={(e) => {
          const raw = parseInt(e.target.value, 10)
          if (Number.isNaN(raw)) {
            onChange(0)
            return
          }
          onChange(Math.min(OVERHOURS_LIMIT_MAX, Math.max(0, raw)))
        }}
      />
    )
  }

  // Read-only display: keep the existing labeled-value-box look so the modal
  // still shows what the value is, just not editable inline.
  if (setting?.type === 'bool') {
    return (
      <FieldsetSwitch
        label={label}
        checked={setting?.value === 'true'}
        disabled
      />
    )
  }

  return (
    <fieldset className="tw-d-form-control tw-w-full">
      <label className="tw-mb-0 tw-px-1 tw-pb-0.5 tw-text-sm tw-font-medium">
        {label}
      </label>
      <div className="tw-flex tw-h-10 tw-items-center tw-rounded tw-border tw-border-[#e5e6e6] tw-bg-[#f8f9fc] tw-px-3 tw-text-sm tw-text-[#1f2937]">
        {formatValue(setting)}
      </div>
    </fieldset>
  )
}

export default EditableSettingField
